/**
 * Knowledge Source Editor Component
 * Drawer for viewing and querying knowledge sources
 */

import {
  Stack,
  Title,
  P,
  Card,
  useToast,
  Icon,
  Button,
  Tag,
  Input
} from "@introist/react-foundation/v2";
import { DrawerHeader } from "../../foundation/templates/Drawer/DrawerHeader";
import { useMemo, useState } from "react";
import { useKnowledgeSourcesWorkspace } from "../hooks/use-knowledge-sources-workspace";
import { knowledgeApi } from "../api/knowledge";
import { useMutation } from "@tanstack/react-query";
import { getKnowledgeSourceIcon } from "./KnowledgeSourceIcon";
import type { QueryKnowledgeResult } from "../api/knowledge";

interface KnowledgeSourceEditorProps {
  knowledgeSourceId: string;
  onClose: () => void;
}

export const KnowledgeSourceEditor = ({
  knowledgeSourceId,
  onClose
}: KnowledgeSourceEditorProps) => {
  const { data: knowledgeSources = [] } = useKnowledgeSourcesWorkspace();
  const toast = useToast();

  const [query, setQuery] = useState("");
  const [showRaw, setShowRaw] = useState(false);
  const [results, setResults] = useState<QueryKnowledgeResult[]>([]);
  const [rawResponse, setRawResponse] = useState<any>(null);

  const knowledgeSource = useMemo(
    () => knowledgeSources.find(k => k.id === knowledgeSourceId),
    [knowledgeSources, knowledgeSourceId]
  );

  const queryMutation = useMutation({
    mutationFn: async (queryText: string) => {
      return knowledgeApi.query(knowledgeSourceId, { query: queryText, limit: 10 });
    },
    onSuccess: (data: QueryKnowledgeResult[]) => {
      setResults(data);
      setRawResponse(data);
      toast.success(`Found ${data.length} results`);
    },
    onError: (error: any) => {
      toast.error(`Query failed: ${error.message}`);
    }
  });

  const handleQuery = async () => {
    if (!query.trim()) {
      toast.error("Please enter a query");
      return;
    }
    await queryMutation.mutateAsync(query);
  };

  if (!knowledgeSource) {
    return (
      <Stack vertical style={{ padding: "var(--spacing-large)" }}>
        <DrawerHeader title="Knowledge Source Not Found" onClose={onClose} />
        <P>This knowledge source could not be found.</P>
      </Stack>
    );
  }

  return (
    <Stack vertical style={{ padding: "var(--spacing-xLarge)", gap: "var(--spacing-xLarge)" }}>
      <DrawerHeader
        title={knowledgeSource.name}
        onClose={onClose}
        icon={
          <div
            style={{
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            {getKnowledgeSourceIcon(knowledgeSource.type)}
          </div>
        }
      />

      {/* Query Interface */}
      <Card>
        <Stack vertical style={{ gap: "var(--spacing-medium)" }}>
          <Title variant="bold">Test Query</Title>
          <Input
            value={query}
            onChange={setQuery}
            placeholder="Enter a question to search this knowledge source..."
            endAdornment={<Icon name="search" />}
            onKeyDown={e => {
              if (e.key === "Enter" && query.trim()) {
                handleQuery();
              }
            }}
          />
          <Stack style={{ justifyContent: "space-between", alignItems: "center" }}>
            <Button
              size="small"
              variant="outlined"
              onClickWithLoading={handleQuery}
              disabled={!query.trim()}
            >
              Run Query
            </Button>
            {results.length > 0 && (
              <Button variant="blended" onClick={() => setShowRaw(!showRaw)} size="small">
                {showRaw ? "Show Results" : "Show Raw"}
              </Button>
            )}
          </Stack>
        </Stack>
      </Card>

      {/* Results */}
      {results.length > 0 && (
        <Stack vertical style={{ gap: "var(--spacing-medium)" }}>
          <Title variant="bold">Results ({results.length})</Title>

          {showRaw ? (
            <Card>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  fontSize: "12px",
                  fontFamily: "monospace"
                }}
              >
                {JSON.stringify(rawResponse, null, 2)}
              </pre>
            </Card>
          ) : (
            <Stack vertical style={{ gap: "var(--spacing-small)" }}>
              {results.map((result, index) => (
                <Card key={index}>
                  <Stack vertical style={{ gap: "var(--spacing-small)" }}>
                    <Stack style={{ justifyContent: "space-between", alignItems: "start" }}>
                      <Stack vertical style={{ gap: "var(--spacing-xxSmall)", flex: 1 }}>
                        <Title variant="bold">{result.docTitle}</Title>
                        {result.link && (
                          <a
                            href={result.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: "12px",
                              color: "var(--palette-foreground-subdued)",
                              textDecoration: "none"
                            }}
                          >
                            {result.link}
                          </a>
                        )}
                      </Stack>
                      <Tag colorVariant="outlined">
                        {parseFloat(result.relevanceScore).toFixed(2)}
                      </Tag>
                    </Stack>
                    <Title small>
                      {result.content.substring(0, 300)}
                      {result.content.length > 300 ? "..." : ""}
                    </Title>
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </Stack>
      )}

      {queryMutation.isIdle && results.length === 0 && (
        <Card variant="blended">
          <Stack
            vertical
            style={{
              alignItems: "center",
              gap: "var(--spacing-small)",
              padding: "var(--spacing-large)"
            }}
          >
            <Icon name="search" dimmed />
            <Title style={{ color: "var(--palette-foreground-subdued)" }}>No queries yet</Title>
            <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
              Enter a question above to test this knowledge source
            </Title>
          </Stack>
        </Card>
      )}
    </Stack>
  );
};
